import { memo, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { CreateTicketFormValues } from '../../../types/form';
import { TicketSystemFieldsKeyMap } from '../../model/types';
import {
    controlledUserFormValue,
    parseMentionInfoFromUserFormValue,
    parseUserIdFromSerializedValue
} from '../../utils/forms/formHelper.parser';
import { transformFormValue, transformMultiUserValue } from '../GrispiInput/components/GrispiMultiUserInput';

export const getMentionedUsersFromComment = (htmlText = '') => {
    const parser = new DOMParser();
    const parsedHTML = parser.parseFromString(htmlText, 'text/html');
    const mentionedUserValues = [
        ...new Set([...parsedHTML.getElementsByClassName('mention')].map((element) => element['dataset']['id']))
    ];
    return mentionedUserValues;
};

const useTrackMentionedUsers = () => {
    const { getValues, setValue } = useFormContext<CreateTicketFormValues>();

    const commentBody = useWatch<CreateTicketFormValues, 'comment.body'>({
        name: 'comment.body'
    });

    const stringifiedMentionedUserList = getMentionedUsersFromComment(commentBody).toString();
    const previousStringifiedMentionedUserList = useRef<string>('');

    useEffect(() => {
        const isMentionListChanged = stringifiedMentionedUserList !== previousStringifiedMentionedUserList.current;

        if (isMentionListChanged) {
            let newFollowersValue: string[] = [];

            const currentFollowersValue: string[] =
                transformFormValue(getValues(`fields.${TicketSystemFieldsKeyMap.followers}`) as string) ?? [];

            if (!stringifiedMentionedUserList) {
                newFollowersValue = currentFollowersValue.filter(
                    (follower) => !parseMentionInfoFromUserFormValue(follower)
                );
            } else {
                const newFollowersValueToAdd: string[] = [];

                const mentionedUserList = stringifiedMentionedUserList.split(',');
                mentionedUserList.forEach((mentionedUser) => {
                    const mentionedUserId = parseUserIdFromSerializedValue(mentionedUser);
                    const mentionedUserInFollowerList = currentFollowersValue.find(
                        (follower) =>
                            parseUserIdFromSerializedValue(controlledUserFormValue(follower)) === mentionedUserId
                    );

                    if (!mentionedUserInFollowerList) {
                        newFollowersValueToAdd.push(mentionedUser);
                    }
                });

                const filteredFollowerList = currentFollowersValue.filter(
                    (follower) =>
                        !(
                            parseMentionInfoFromUserFormValue(follower) &&
                            !mentionedUserList
                                .map(parseUserIdFromSerializedValue)
                                .includes(parseUserIdFromSerializedValue(controlledUserFormValue(follower)))
                        )
                );

                newFollowersValue = [...filteredFollowerList, ...newFollowersValueToAdd];
            }

            setValue(`fields.${TicketSystemFieldsKeyMap.followers}`, transformMultiUserValue(newFollowersValue));
            previousStringifiedMentionedUserList.current = stringifiedMentionedUserList;
        }
    }, [stringifiedMentionedUserList, getValues, setValue]);
};

const MentionedUserController = () => {
    useTrackMentionedUsers();
    return null;
};

export default memo(MentionedUserController);
